import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TreeView from '@Services/TreeView';
import { ButtonWithIcon } from '@els/els-react--button';
import allowedTags from '@Services/AllowedTag';
import { ALLOWED_TAGS_ARRAY } from '../TreeViewConstants';

import './TreeViewSelected.scss';

const CONTAINER_SELECTOR = 'treeViewSelected-container';

function TreeViewSelected({ selectedTaxonomies, selectedCustomItems, selectedListExpanded, onToggle, onRemove }) {
  const [isMoreVisible, setIsMoreVisible] = useState(false);
  // true  → custom list rendered first (a custom item was the last thing added)
  // false → taxonomy list rendered first (default)
  const [customFirst, setCustomFirst] = useState(false);

  // Derive stable primitives from the array contents so change detection works
  // even when a parent mutates an array in-place (same reference, different
  // contents) — a pattern that fools React's own reference-equality dep check.
  //
  // taxMaxOrder  – highest selectionOrder stamp in the taxonomy list; changes
  //               whenever a taxonomy is selected or re-selected regardless of
  //               whether the array reference changed.
  // customLastItem – last element of the custom list; together with length this
  //               catches both appends and removals of custom items.
  const taxLength = selectedTaxonomies.length;
  const taxMaxOrder = selectedTaxonomies.reduce((max, t) => Math.max(max, t?.selectionOrder ?? 0), 0);
  const customLength = selectedCustomItems.length;
  const customLastItem = selectedCustomItems[customLength - 1];

  // Refs hold the previous render's primitive values for comparison inside the
  // effect.  Initialised to the first-render values so the initial mount is
  // treated as "nothing changed yet" (both lists start equal to themselves).
  const prevTaxLengthRef = useRef(taxLength);
  const prevTaxMaxOrderRef = useRef(taxMaxOrder);
  const prevCustomLengthRef = useRef(customLength);
  const prevCustomLastItemRef = useRef(customLastItem);

  function getIsMoreVisible() {
    const container = document.getElementById(CONTAINER_SELECTOR);

    if (!container) {
      return false;
    }

    const containerWidth = container.offsetWidth;

    const isWidthAcceptable = TreeView.isElementsWidthMoreThenMaximal(container.children, containerWidth);

    return selectedListExpanded || isWidthAcceptable;
  }

  useEffect(() => {
    const taxonomiesChanged =
      taxLength !== prevTaxLengthRef.current || taxMaxOrder !== prevTaxMaxOrderRef.current;

    const customChanged =
      customLength !== prevCustomLengthRef.current || customLastItem !== prevCustomLastItemRef.current;

    prevTaxLengthRef.current = taxLength;
    prevTaxMaxOrderRef.current = taxMaxOrder;
    prevCustomLengthRef.current = customLength;
    prevCustomLastItemRef.current = customLastItem;

    if (selectedTaxonomies || selectedCustomItems) {
      setIsMoreVisible(getIsMoreVisible());
    }

    // Only flip the order when exactly one list changes so that an unrelated
    // re-render doesn't reset the ordering.
    if (customChanged && !taxonomiesChanged) {
      setCustomFirst(true);
    } else if (taxonomiesChanged && !customChanged) {
      setCustomFirst(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxLength, taxMaxOrder, customLength, customLastItem]);

  const toggleExpanded = () => {
    onToggle(!selectedListExpanded);
  };

  const getAllowedTags = (value) => allowedTags(value, ALLOWED_TAGS_ARRAY);

  const getMoreIcon = () => (selectedListExpanded ? 'Minus' : 'Plus');

  // Focuses the previous item in the combined list, or the next if there is no previous
  function focusPrevOrNextItem(currentIndex) {
    const listItems = document.querySelectorAll('.treeViewSelected__list_item button') || [];
    if (listItems.length === 0) return;
    if (currentIndex > 0) {
      listItems[currentIndex - 1]?.focus();
    } else if (listItems.length > 1) {
      listItems[1]?.focus();
    }
  }

  return (
    <div
      className={classnames('treeViewSelected', {
        'treeViewSelected-expanded': selectedListExpanded,
      })}>
      <div className="treeViewSelected__title">
        <h4 className="u-els-font-size-base u-els-line-height-default">Selected search items:</h4>
        {isMoreVisible && (
          <ButtonWithIcon
            data-e2e="tree-view-selected-showMore"
            type="link"
            size="small"
            linkIconSize="s"
            sprite={getMoreIcon()}
            iconOnly
            onClick={toggleExpanded}
            className="e2e-tree-view-selected-showMore">
            {selectedListExpanded ? 'Less' : 'More'}
          </ButtonWithIcon>
        )}
      </div>
      <ul id="treeViewSelected-container" className="treeViewSelected__list">
        {(() => {
          // Render both lists reversed so the newest item is always at the top.
          // The list that changed most recently is rendered first so that item
          // appears above everything else.
          const taxonomyList = selectedTaxonomies
            .slice()
            .reverse()
            .map((taxonomy, reversedIndex) => {
              // DOM position depends on whether custom list is rendered before us.
              const domIndex = customFirst ? selectedCustomItems.length + reversedIndex : reversedIndex;
              return (
                <li className="treeViewSelected__list_item" key={`selected-taxonomies-${taxonomy.id}`}>
                  <span className="treeViewSelected__list_item_label" title={taxonomy.name}>
                    {getAllowedTags(taxonomy.name)}
                  </span>
                  <ButtonWithIcon
                    id={`remove-selected-taxonomies-${taxonomy.id}`}
                    data-e2e="tree-view-selected-item-remove"
                    type="link"
                    linkIconSize="xs"
                    sprite="Close"
                    iconOnly
                    onClick={() => {
                      focusPrevOrNextItem(domIndex);
                      onRemove(taxonomy);
                    }}
                    className="e2e-tree-view-selected-item-remove">
                    Remove
                  </ButtonWithIcon>
                </li>
              );
            });

          const customList = selectedCustomItems
            .slice()
            .reverse()
            .map((custom, reversedIndex) => {
              // Recover original array index so onRemove can identify the item.
              const originalIndex = selectedCustomItems.length - 1 - reversedIndex;
              // DOM position depends on whether taxonomy list is rendered before us.
              const domIndex = customFirst ? reversedIndex : selectedTaxonomies.length + reversedIndex;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <li className="treeViewSelected__list_item" key={`selected-taxonomies-${custom}-${originalIndex}`}>
                  <span className="treeViewSelected__list_item_label" title={custom}>
                    {getAllowedTags(custom)}
                  </span>
                  <ButtonWithIcon
                    id={`remove-selected-custom-${originalIndex}`}
                    data-e2e="tree-view-selected-item-remove"
                    type="link"
                    size="small"
                    linkIconSize="s"
                    sprite="Close"
                    iconOnly
                    onClick={() => {
                      focusPrevOrNextItem(domIndex);
                      onRemove({ value: custom, index: originalIndex, custom: true });
                    }}
                    className="e2e-tree-view-selected-item-remove">
                    Cancel
                  </ButtonWithIcon>
                </li>
              );
            });

          return customFirst ? (
            <>
              {customList}
              {taxonomyList}
            </>
          ) : (
            <>
              {taxonomyList}
              {customList}
            </>
          );
        })()}
      </ul>
    </div>
  );
}

TreeViewSelected.propTypes = {
  selectedTaxonomies: PropTypes.array,
  selectedCustomItems: PropTypes.array,
  selectedListExpanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default TreeViewSelected;
